// What a Calendar board shows in this browser: its view (month, week, day or the list) and whether the calendars column is
// out, kept per board in this browser; the day whose week (or month, or day) is in view, for this visit (a Calendar
// always opens on today); and the event open in its card, or a new one being made (a draft), not saved until it is.

import { useEffect, useState } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { weekStartsOn, type AgendaView, type Shown } from "../../../shared/agenda";
import { dayOf } from "../../../shared/days";
import { regionDayFirst } from "../../../shared/region";
import type { TextColor } from "../../../shared/textColors";
import type { RepeatRule } from "../../../shared/repeat";
import type { Entry, ID } from "../../../shared/types";
import { boardEntries, useStore } from "../../state/store";
import { readStoredJson, writeStored } from "../../lib/stored";

interface Kept {
  view: AgendaView;
  /** the calendars column, down the left */
  side: boolean;
}

const KEY = "cb:agenda";
const DEFAULT: Kept = { view: "week", side: true };

function load(): Record<ID, Kept> {
  const d = readStoredJson<Record<ID, Partial<Kept>>>(KEY, {});
  const out: Record<ID, Kept> = {};
  for (const [id, k] of Object.entries(d)) out[id] = { view: k.view === "month" || k.view === "day" || k.view === "list" ? k.view : "week", side: k.side !== false };
  return out;
}

/** A new event being made: on the grid, not saved until the card saves it. Times on the viewer's clock. */
export interface Draft {
  boardId: ID;
  allDay: boolean;
  /** "YYYY-MM-DDTHH:MM", or a day when all day */
  start: string;
  end: string;
  title: string;
  location?: string;
  notes?: string;
  color?: TextColor;
  /** the Google calendars it also goes into, by key; none chosen: the board's default */
  cals?: string[];
  repeat?: RepeatRule;
  /** a Google calendar's new event: a Google Meet call, and guests Google emails when it is saved (step 5) */
  meet?: boolean;
  guests?: string[];
}

interface AgendaUi {
  kept: Record<ID, Kept>;
  /** the day in view, per board, for this visit */
  at: Record<ID, string>;
  /** the event open in its card, where it was clicked; or the draft's card; a Google event's card holds the event as shown */
  card: { id: ID | "draft"; at: { x: number; y: number; w: number; h: number } | null; shown?: Shown } | null;
  draft: Draft | null;
  /** an event's own menu, from a right-click on it */
  menu: { id: ID; x: number; y: number; shown?: Shown } | null;
}

export const useAgendaUi = create<AgendaUi>(() => ({ kept: load(), at: {}, card: null, draft: null, menu: null }));

let saved = JSON.stringify(useAgendaUi.getState().kept);
useAgendaUi.subscribe((s) => {
  const next = JSON.stringify(s.kept);
  if (next === saved) return;
  saved = next;
  writeStored(KEY, next); // kept for this visit only when storage is out of reach
});

export const today = () => dayOf(new Date());

/**
 * The day a week starts on here: read once from the browser's language, except a browser that says American English in
 * a region that writes the day first (a UK computer set up in en-US), where the week starts on the region's Monday, as
 * its dates and times follow the region (Fixes 9).
 */
export const startsOn = (() => {
  const day = weekStartsOn();
  const lang = typeof navigator !== "undefined" ? navigator.language : "";
  return day === 0 && /^en(-US)?$/i.test(lang) && regionDayFirst() ? 1 : day;
})();

export function useKept(boardId: ID): Kept {
  return useAgendaUi((s) => s.kept[boardId] ?? DEFAULT);
}

export function setKept(boardId: ID, patch: Partial<Kept>) {
  useAgendaUi.setState((s) => ({ kept: { ...s.kept, [boardId]: { ...(s.kept[boardId] ?? DEFAULT), ...patch } } }));
}

export function setView(boardId: ID, view: AgendaView) {
  setKept(boardId, { view });
}

export function useAt(boardId: ID): string {
  return useAgendaUi((s) => s.at[boardId]) ?? today();
}

export function setAt(boardId: ID, day: string) {
  useAgendaUi.setState((s) => ({ at: { ...s.at, [boardId]: day } }));
}

const rectOf = (el: Element | null | undefined) => {
  const r = el?.getBoundingClientRect();
  return r ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
};

/** Opens an event's card beside what was clicked; a Google event's card is given the event as shown. */
export function openEvent(id: ID, el?: Element | null, shown?: Shown) {
  // a layer item (step 6) opens on its own board, as the chat's go-there links do
  const layer = shown?.meta?.layer;
  if (layer) return void import("../../state/goto").then((m) => m.goTo({ board: layer.board, entry: layer.entry }));
  useAgendaUi.setState({ card: { id, at: rectOf(el), ...(shown && shown.cal !== "board" ? { shown } : {}) }, draft: null, menu: null });
}

/** Starts a new event on the grid, its card open beside it once it is drawn. */
export function startDraft(d: Draft) {
  useAgendaUi.setState({ draft: d, card: null, menu: null });
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const el = document.querySelector("[data-ag-draft]");
      useAgendaUi.setState({ card: { id: "draft", at: rectOf(el) } });
    }),
  );
}

export function patchDraft(patch: Partial<Draft>) {
  useAgendaUi.setState((s) => (s.draft ? { draft: { ...s.draft, ...patch } } : {}));
}

export function closeCard() {
  useAgendaUi.setState({ card: null, draft: null });
}

export function eventMenu(at: AgendaUi["menu"]) {
  useAgendaUi.setState({ menu: at });
}

/** A Calendar board's entries: its events and its settings. */
export function useAgendaEntries(boardId: ID): Entry[] {
  return useStore(useShallow((s) => boardEntries(s, boardId).filter((e) => e.t === "event" || e.t === "calset")));
}

/** Now, where the user is: today and the minute of the day. Moves on every 20 seconds, and at midnight. */
export function useNow(): { today: string; min: number } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(t);
  }, []);
  const d = new Date(now);
  return { today: dayOf(d), min: d.getHours() * 60 + d.getMinutes() };
}
